import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IDataContext } from '../../../core/component/list/component.interface';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../core/service/language.service';
import { AmortizationPlan } from '../../model/amortization-plan';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';

@Component({
  standalone: false,
  selector: 'app-cancel-amortization-plan-form',
  templateUrl: './cancel-amortization-plan-form.component.html',
  styleUrls: ['./cancel-amortization-plan-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CancelAmortizationPlanFormComponent implements IDataContext, OnDestroy {
  form: FormGroup;
  working = false;

  private readonly _subscription = new Subscription();
  private _dataContext: AmortizationPlan;

  constructor(
    private readonly _service: AmortizationPlanService,
    private readonly _modalService: ActionWindowService,
    private readonly _fb: FormBuilder,
    private readonly _fc: FormControlExFactory,
    private readonly _languageService: LanguageService
  ) {
    this.form = this._fb.group({
      id: this._fc.createHidden(''),
      sendInvoicesToDebtCollection: this._fb.control(false),
      reason: this._fc.createMaxLength('AMORTIZATION_PLAN.CANCEL.REASON', null, 500),
    });

    this._subscription.add(
      this.form.controls['sendInvoicesToDebtCollection'].valueChanges.subscribe(sendToDebtCollection => {
        this.updateReasonState(!!sendToDebtCollection);
      })
    );
  }

  @Input() set dataContext(value: AmortizationPlan) {
    this._dataContext = value;
    this.form.controls['id'].setValue(value?.id ?? null);
  }

  get dataContext(): AmortizationPlan {
    return this._dataContext;
  }

  get canSendInvoicesToDebtCollection(): boolean {
    return !!this.dataContext?.canSendInvoicesToDebtCollection;
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  cancel(): void {
    this._modalService.close();
  }

  onSubmit(): void {
    if (this.form.invalid || this.working) {
      return;
    }

    this.working = true;
    this._subscription.add(
      this._service.cancelPlan(this.form.value).subscribe({
        next: plan => {
          this._modalService.complete(plan);
          this._modalService.close();
        },
        error: () => {
          this.working = false;
        },
      })
    );
  }

  private updateReasonState(sendToDebtCollection: boolean): void {
    const reasonControl = this.form.controls['reason'];
    if (sendToDebtCollection && !reasonControl.value) {
    reasonControl.setValue(this._languageService.instant('AMORTIZATION_PLAN.CANCEL.DEFAULT_REASON'));
    }

    reasonControl.setValidators(sendToDebtCollection ? [Validators.required, Validators.maxLength(500)] : [Validators.maxLength(500)]);
    reasonControl.updateValueAndValidity();
  }
}
